/**
 * components/ui/design.tsx
 * ------------------------------------------------------------------
 * THE PORTAL DESIGN LANGUAGE — the finance screens' LAYOUT, in the Practice
 * & Tests PALETTE. Chosen by the school on 2026-09-13, first for the Teaching
 * and Communications screens, and adopted as the target for EVERY staff
 * screen: a screen that is touched for any reason is moved onto this kit as
 * part of that work (see CLAUDE.md, "The design language").
 *
 * Before this, the four screens under Teaching looked like three products:
 * Practice & Tests on warm paper with class pills, Schemes of Work and Lesson
 * Plans as plain white cards with no header, and Curriculum in the older blue
 * gradient hero. A teacher moves between all four in one sitting.
 *
 * TWO DECISIONS, KEPT APART:
 *
 *   Structure comes from `features/finance/ui` — the icon-tile page header,
 *   the stat strip read off an accent bar, flat hairline panels, firm fields,
 *   and the same button vocabulary. It is a DROP-IN: every export carries the
 *   finance kit's name and props, so a screen adopts the language by changing
 *   an import line, exactly as fifty-odd finance screens did.
 *
 *   Colour comes from the student portal's `.study-theme` tokens (index.css),
 *   so a teacher's view of the study engine and the engine itself read as one
 *   product. Surfaces use the tokens, NEVER literal slate/gray: a literal
 *   `bg-white` or `text-gray-500` fights the paper ground in light mode and
 *   the charcoal one in dark.
 *
 * `Page` is the one thing a screen must add rather than swap: it is
 * the element that scopes the tokens. Without it the kit's `var(--sp-*)`
 * resolves to nothing and panels turn transparent.
 *
 * Purely visual. No logic, no data.
 * ------------------------------------------------------------------ */
import type { ReactNode } from 'react';
import { BookOpen, FileDown, FileSpreadsheet, Sheet, type LucideIcon } from 'lucide-react';

/* ------------------------------- palette ------------------------------- */

/** The identifying icon tile. The school's blue, as in finance and in the
 *  study portal's own accent — so Teaching stays inside the school palette
 *  rather than inventing a department colour. */
const TILE = {
  navy: 'bg-gradient-to-br from-primary to-primaryLight',
  gold: 'bg-gradient-to-br from-secondary to-secondaryLight',
} as const;

/** Accent per tone — the finance kit's names, so a call site copied from a
 *  finance screen means the same thing here. */
const ACCENT: Record<string, { bar: string; text: string }> = {
  slate: { bar: 'border-l-[var(--sp-line-2)]', text: 'text-ink dark:text-slate-100' },
  blue: { bar: 'border-l-primary', text: 'text-primary dark:text-primarySoft' },
  navy: { bar: 'border-l-primary', text: 'text-primary dark:text-primarySoft' },
  gold: { bar: 'border-l-secondary', text: 'text-secondaryDeep dark:text-secondary' },
  emerald: { bar: 'border-l-emerald-500', text: 'text-emerald-600 dark:text-emerald-400' },
  green: { bar: 'border-l-emerald-500', text: 'text-emerald-600 dark:text-emerald-400' },
  red: { bar: 'border-l-red-500', text: 'text-red-600 dark:text-red-400' },
  warn: { bar: 'border-l-amber-500', text: 'text-amber-600 dark:text-amber-400' },
  amber: { bar: 'border-l-amber-500', text: 'text-amber-600 dark:text-amber-400' },
  purple: { bar: 'border-l-purple-500', text: 'text-purple-600 dark:text-purple-300' },
};
const accentFor = (tone?: string) => ACCENT[tone ?? 'slate'] ?? ACCENT.slate;

/** Every container's surface: the card token and a hairline, no heavy shadow
 *  — on paper a shadow reads as a sticker, not a panel. */
export const PANEL = 'bg-[var(--sp-card)] border border-[var(--sp-line)]';

/* ------------------------------- the page ------------------------------ */

/**
 * The page ground. REQUIRED around every Teaching screen: it is the element
 * that scopes the `--sp-*` tokens. The negative margin pulls the paper to the
 * edges of the shell's content area, exactly as Practice & Tests does, so the
 * warm ground does not float as a box inside a slate one.
 */
export function Page({ children, className = '', wide = false, bare = false }: {
  children: ReactNode;
  className?: string;
  /** Workbenches (a scheme editor, a lesson plan) need the room. */
  wide?: boolean;
  /**
   * Scope the palette WITHOUT taking over the layout — for a converted
   * component embedded inside a host screen that is not converted yet (the
   * parent inbox inside Parent Onboarding). Outside a scope the `--sp-*`
   * tokens resolve to nothing and panels go transparent, so an embedded
   * component must still bring one.
   */
  bare?: boolean;
}) {
  if (bare) return <div className={`study-theme ${className}`}>{children}</div>;
  return (
    <div className={`study-theme -m-4 min-h-full bg-[var(--sp-paper)] p-4 sm:-m-6 sm:p-6 ${className}`}>
      <div className={`mx-auto ${wide ? 'max-w-7xl' : 'max-w-6xl'}`}>{children}</div>
    </div>
  );
}

/* -------------------------------- panel -------------------------------- */

/** Drop-in for `SoftCard`. `hover` is accepted and ignored, as in finance. */
export function SoftCard({
  children, className = '', hover: _hover = true, pad = 'p-5',
}: { children: ReactNode; className?: string; hover?: boolean; pad?: string }) {
  return <div className={`rounded-xl ${PANEL} ${pad} ${className}`}>{children}</div>;
}

/** A panel with a coloured left edge — a verdict, a warning, a total. */
export function AccentPanel({
  children, className = '', tone = 'navy', pad = 'p-4',
}: { children: ReactNode; className?: string; tone?: string; pad?: string }) {
  return (
    <div className={`rounded-r-xl ${PANEL} border-l-4 ${accentFor(tone).bar} ${pad} ${className}`}>
      {children}
    </div>
  );
}

/* ------------------------------ stat tile ------------------------------ */

/** Drop-in for `StatChip`: a figure read left to right off an accent bar. */
export function StatChip({
  tone = 'slate', value, label, icon: Icon,
}: { tone?: string; value: ReactNode; label: string; icon?: LucideIcon }) {
  const a = accentFor(tone);
  return (
    <div className={`rounded-r-xl ${PANEL} border-l-4 ${a.bar} flex min-w-0 items-center gap-3 px-4 py-3`}>
      {Icon && (
        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-[var(--sp-paper)]">
          <Icon size={18} className={a.text} />
        </div>
      )}
      <div className="min-w-0">
        <p className={`truncate text-2xl font-bold leading-none tabular-nums ${a.text}`}>{value}</p>
        <p className="mt-1 text-[11px] text-[var(--sp-muted)]">{label}</p>
      </div>
    </div>
  );
}

/** A row of stat chips that wraps sensibly from phone to desktop. */
export function StatStrip({ children }: { children: ReactNode }) {
  return <div className="mb-5 grid grid-cols-2 gap-3 lg:grid-cols-4">{children}</div>;
}

/* ----------------------------- page header ----------------------------- */

/** Drop-in for `PageHeader`, with an optional icon and an eyebrow line —
 *  the uppercase context label Practice & Tests uses above a class name. */
export function PageHeader({
  title, subtitle, eyebrow, actions, icon: Icon = BookOpen, tone = 'navy',
}: {
  title: string;
  /** ReactNode, not string: the old GradientHero took rich subtitles, and a
   *  drop-in must accept everything the thing it replaces accepted. */
  subtitle?: ReactNode;
  eyebrow?: string;
  actions?: ReactNode;
  icon?: LucideIcon;
  tone?: keyof typeof TILE;
}) {
  return (
    <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
      <div className="flex min-w-0 items-center gap-3">
        <div className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-xl text-white shadow-md ${TILE[tone]}`}>
          <Icon size={22} />
        </div>
        <div className="min-w-0">
          {eyebrow && (
            <p className="text-[11px] font-semibold uppercase tracking-[0.1em] text-[var(--sp-muted)]">{eyebrow}</p>
          )}
          <h1 className="text-xl font-bold leading-tight tracking-tight text-ink dark:text-slate-100">{title}</h1>
          {subtitle && <div className="text-sm text-[var(--sp-muted)]">{subtitle}</div>}
        </div>
      </div>
      {actions && <div className="flex flex-wrap items-center gap-2">{actions}</div>}
    </div>
  );
}

/** A section heading inside a panel: the same tile, smaller. */
export function PanelHeader({
  icon: Icon, title, subtitle, right, tone = 'navy',
}: { icon: LucideIcon; title: string; subtitle?: string; right?: ReactNode; tone?: keyof typeof TILE }) {
  return (
    <div className="flex items-center justify-between gap-3 border-b border-[var(--sp-line)] px-4 py-3">
      <div className="flex min-w-0 items-center gap-3">
        <div className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-lg text-white shadow-sm ${TILE[tone]}`}>
          <Icon size={18} />
        </div>
        <div className="min-w-0">
          <h3 className="truncate font-bold leading-tight text-ink dark:text-slate-100">{title}</h3>
          {subtitle && <p className="truncate text-[11px] text-[var(--sp-muted)]">{subtitle}</p>}
        </div>
      </div>
      {right}
    </div>
  );
}

/* --------------------------------- tabs -------------------------------- */

/**
 * The segmented tabs a Teaching screen uses to switch view ("Mine /
 * Department", "Plan / Deliver"). One definition, so the same control is the
 * same size and colour on every screen rather than four hand-rolled ones.
 */
export function Tabs<T extends string | boolean>({
  value, onChange, items,
}: {
  value: T;
  onChange: (v: T) => void;
  items: { id: T; label: string; badge?: number }[];
}) {
  return (
    <div className="inline-flex max-w-full flex-wrap gap-1 rounded-xl border border-[var(--sp-line)] bg-[var(--sp-card)] p-1" role="tablist">
      {items.map((t) => {
        const on = t.id === value;
        return (
          <button
            key={String(t.id)}
            type="button"
            role="tab"
            aria-selected={on}
            onClick={() => onChange(t.id)}
            className={`rounded-lg px-3 py-1.5 text-sm font-semibold transition ${
              on ? 'bg-primary text-white shadow-sm' : 'text-[var(--sp-body)] hover:bg-[var(--sp-paper)]'
            }`}
          >
            {t.label}
            {!!t.badge && (
              <span className={`ml-1.5 rounded-full px-1.5 py-0.5 text-[10px] ${on ? 'bg-white/25 text-white' : 'bg-primary text-white'}`}>
                {t.badge}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}

/* -------------------------------- fields -------------------------------- */

/** Drop-in for `glassFieldClass`: the finance kit's firm, compact field on the
 *  card token. Keeps the focus ring for keyboard users. */
export function glassFieldClass(extra = '') {
  return `w-full px-3 py-2 rounded-lg border-2 bg-[var(--sp-card)] border-[var(--sp-line)] text-ink dark:text-slate-100 text-sm hover:border-[var(--sp-line-2)] focus:border-primaryLight focus:ring-4 focus:ring-primary/10 focus:outline-none transition-colors ${extra}`;
}

/** The primary action, in the school's blue — the same button as finance. */
export const btnPrimary =
  'px-4 py-2 rounded-lg bg-primary text-white text-sm font-semibold inline-flex items-center gap-1.5 hover:bg-primaryLight disabled:opacity-40 disabled:cursor-not-allowed transition-colors';

/** The quiet action beside it. */
export const btnGhost =
  'px-4 py-2 rounded-lg border-2 border-[var(--sp-line)] bg-[var(--sp-card)] text-[var(--sp-body)] text-sm font-semibold inline-flex items-center gap-1.5 hover:border-[var(--sp-line-2)] disabled:opacity-40 disabled:cursor-not-allowed transition-colors';

/** Export colours are finance's on purpose: a download should look the same,
 *  and look unlike the button that changes something, on every screen. */
const btnExport = 'px-3 py-2 rounded-lg border-2 text-sm font-semibold inline-flex items-center gap-1.5 disabled:opacity-40 disabled:cursor-not-allowed transition-colors';
export const btnPdf = `${btnExport} border-rose-200 bg-rose-50 text-rose-700 hover:bg-rose-100 hover:border-rose-300 dark:border-rose-900/60 dark:bg-rose-950/40 dark:text-rose-300 dark:hover:bg-rose-950/70`;
export const btnCsv = `${btnExport} border-emerald-200 bg-emerald-50 text-emerald-700 hover:bg-emerald-100 hover:border-emerald-300 dark:border-emerald-900/60 dark:bg-emerald-950/40 dark:text-emerald-300 dark:hover:bg-emerald-950/70`;

export function ExportBar({
  onPdf, onCsv, onExcel, disabled = false, label = 'Export',
}: {
  onPdf?: () => void;
  onCsv?: () => void;
  /** An .xlsx workbook — for exports that need several sheets. */
  onExcel?: () => void;
  disabled?: boolean;
  label?: string;
}) {
  return (
    <div className="flex items-center gap-2" role="group" aria-label={label}>
      {onPdf && (
        <button type="button" onClick={onPdf} disabled={disabled} className={btnPdf} title="Download as PDF">
          <FileDown size={15} /> PDF
        </button>
      )}
      {onCsv && (
        <button type="button" onClick={onCsv} disabled={disabled} className={btnCsv} title="Download as a spreadsheet">
          <Sheet size={15} /> CSV
        </button>
      )}
      {onExcel && (
        <button type="button" onClick={onExcel} disabled={disabled} className={btnCsv} title="Download as an Excel workbook">
          <FileSpreadsheet size={15} /> Excel
        </button>
      )}
    </div>
  );
}

/** A modal panel on the card token — the four hand-rolled dialogs under
 *  Teaching were all `bg-white rounded-2xl`, white on a charcoal page in dark. */
export const dialogPanel = `w-full rounded-xl ${PANEL} p-5 shadow-xl`;
